import { GameState } from './GameState';
import { Deck } from './Deck';
import type { RegretsData, Cost } from '../types';
import { getResourceIcon, replaceResourceNamesWithSymbols } from '../utils/Icons';

export class RegretsPhase {
  private gameState: GameState;
  private deck: Deck;
  private regretsData: RegretsData;
  private onComplete: () => void;
  private currentStep: number = 0;
  
  constructor(
    gameState: GameState,
    deck: Deck,
    regretsData: RegretsData,
    onComplete: () => void
  ) {
    this.gameState = gameState;
    this.deck = deck;
    this.regretsData = regretsData;
    this.onComplete = onComplete;
  }
  
  async start(): Promise<void> {
    this.currentStep = 0;
    await this.executeStep();
  }
  
  private async executeStep(): Promise<void> {
    switch (this.currentStep) {
      case 0:
        await this.step1_LoseHealth();
        break;
      case 1:
        await this.step2_PayBills();
        break;
      case 2:
        await this.step3_BasicNeeds();
        break;
      case 3:
        await this.step4_Connections();
        break;
      case 4:
        this.step5_Complete();
        break;
    }
  }
  
  // Step 1: Lose Health = Health - Bad Habits (can go negative, triggering health collapse)
  private async step1_LoseHealth(): Promise<void> {
    const step1 = this.regretsData.step1_lose_health;
    const badHabits = this.gameState.getResource('bad_habit');
    const previousHealth = this.gameState.getResource('health');
    const newHealthValue = previousHealth - badHabits;
    
    // Set health to new value (this will trigger health collapse if it goes to 0 or below)
    this.gameState.setResource('health', newHealthValue);
    
    // Check if health collapsed (setResource will have reset health to 1 and reduced sanity)
    const healthCollapsed = this.gameState.checkHealthCollapse();
    const currentHealth = this.gameState.getResource('health');
    
    let message = '';
    if (healthCollapsed) {
      message = `Your bad habits catch up with you. Your Health drops to 0, causing your Sanity to suffer. Your condition worsens.`;
    } else {
      message = step1.message_template
        .replace('{previousHealth}', previousHealth.toString())
        .replace('{newHealth}', currentHealth.toString());
    }
    
    // Show Bad Habits in the modal
    const badHabitsDisplay = `Bad Habits: ${badHabits} ${getResourceIcon('bad_habit')}`;
    const fullMessage = `${badHabitsDisplay}\n\n${message}`;
    
    await this.showModal(
      step1.title,
      fullMessage,
      []
    );
    
    this.currentStep = 1;
    await this.executeStep();
  }
  
  // Step 2: Pay Money (or lose Health per unpaid Money)
  private async step2_PayBills(): Promise<void> {
    const step2 = this.regretsData.step2_pay_bills;
    const requiredMoney = step2.required_money;
    const currentMoney = this.gameState.getResource('money');
    const moneyToPay = Math.min(requiredMoney, currentMoney);
    const moneyShort = requiredMoney - moneyToPay;
    const healthLoss = moneyShort * (step2.health_loss_per_unpaid_money || 1);
    
    this.gameState.modifyResource('money', -moneyToPay);
    if (moneyShort > 0) {
      this.gameState.modifyResource('health', -healthLoss);
    }
    
    let message = step2.message_templates.start
      .replace('{requiredMoney}', `${requiredMoney} 💰`);
    
    if (moneyToPay > 0) {
      message += step2.message_templates.paid
        .replace('{moneyPaid}', `${moneyToPay} 💰`);
    }
    
    if (moneyShort > 0) {
      message += step2.message_templates.shortfall
        .replace('{moneyShort}', `${moneyShort} 💰`)
        .replace('{healthLoss}', `${healthLoss} ❤️`);
    } else {
      message += step2.message_templates.covered;
    }
    
    await this.showModal(
      step2.title,
      message,
      []
    );
    
    // Check if health collapsed (checkHealthCollapse already clears the flag)
    this.gameState.checkHealthCollapse();
    
    this.currentStep = 2;
    await this.executeStep();
  }
  
  // Step 3: Basic Needs choice
  private async step3_BasicNeeds(): Promise<void> {
    const step3 = this.regretsData.step3_basic_needs;
    const choice = await this.showChoiceModal(
      step3.title,
      step3.message,
      step3.choices.map(c => ({
        name: c.name,
        cost: c.cost,
        effects: c.effects
      }))
    );
    
    const selectedChoice = step3.choices[choice];
    
    if (selectedChoice.cost) {
      // Pay resources from cost
      const cost = selectedChoice.cost;
      let totalSanityLoss = 0;
      
      // Only Health and Sanity can go negative (paid with Sanity fallback)
      // Other resources must be fully available
      for (const [resource, amount] of Object.entries(cost)) {
        if (amount && amount > 0) {
          const resourceType = resource as any;
          const currentAmount = this.gameState.getResource(resourceType);
          
          // Health and Sanity can be paid partially, with shortfall paid by Sanity
          if (resourceType === 'health' || resourceType === 'sanity') {
            const toPay = Math.min(amount, currentAmount);
            const shortfall = amount - toPay;
            
            this.gameState.modifyResource(resourceType, -toPay);
            if (shortfall > 0) {
              totalSanityLoss += shortfall;
            }
          } else {
            // Other resources must be fully available (should have been validated by showChoiceModal)
            // But as a safety check, only pay what's available
            const toPay = Math.min(amount, currentAmount);
            if (toPay < amount) {
              console.error(`Insufficient ${resource} to pay cost. This should have been prevented.`);
            }
            this.gameState.modifyResource(resourceType, -toPay);
          }
        }
      }
      
      // Apply effects
      if (selectedChoice.effects) {
        for (const effect of selectedChoice.effects) {
          for (const [resource, amount] of Object.entries(effect)) {
            if (amount !== undefined && amount !== 0) {
              this.gameState.modifyResource(resource as any, amount);
            }
          }
        }
      }
      
      // Pay with Sanity if needed (only for Health and Sanity shortfalls)
      if (totalSanityLoss > 0) {
        this.gameState.modifyResource('sanity', -totalSanityLoss);
      }
    } else {
      // Apply effects only (no cost)
      if (selectedChoice.effects) {
        for (const effect of selectedChoice.effects) {
          for (const [resource, amount] of Object.entries(effect)) {
            if (amount !== undefined && amount !== 0) {
              this.gameState.modifyResource(resource as any, amount);
            }
          }
        }
      }
    }
    
    this.currentStep = 3;
    await this.executeStep();
  }
  
  // Step 4: Connections choice
  private async step4_Connections(): Promise<void> {
    const step4 = this.regretsData.step4_connections;
    const choice = await this.showChoiceModal(
      step4.title,
      step4.message,
      step4.choices.map(c => ({
        name: c.name,
        cost: c.cost,
        effects: c.effects
      }))
    );
    
    const selectedChoice = step4.choices[choice];
    
    if (selectedChoice.cost) {
      // Pay resources from cost
      const cost = selectedChoice.cost;
      let totalSanityLoss = 0;
      
      // Only Health and Sanity can go negative (paid with Sanity fallback)
      // Other resources must be fully available
      for (const [resource, amount] of Object.entries(cost)) {
        if (amount && amount > 0) {
          const resourceType = resource as any;
          const currentAmount = this.gameState.getResource(resourceType);
          
          // Health and Sanity can be paid partially, with shortfall paid by Sanity
          if (resourceType === 'health' || resourceType === 'sanity') {
            const toPay = Math.min(amount, currentAmount);
            const shortfall = amount - toPay;
            
            this.gameState.modifyResource(resourceType, -toPay);
            if (shortfall > 0) {
              totalSanityLoss += shortfall;
            }
          } else {
            // Other resources must be fully available (should have been validated by showChoiceModal)
            // But as a safety check, only pay what's available
            const toPay = Math.min(amount, currentAmount);
            if (toPay < amount) {
              console.error(`Insufficient ${resource} to pay cost. This should have been prevented.`);
            }
            this.gameState.modifyResource(resourceType, -toPay);
          }
        }
      }
      
      // Apply effects
      if (selectedChoice.effects) {
        for (const effect of selectedChoice.effects) {
          for (const [resource, amount] of Object.entries(effect)) {
            if (amount !== undefined && amount !== 0) {
              this.gameState.modifyResource(resource as any, amount);
            }
          }
        }
      }
      
      // Pay with Sanity if needed (only for Health and Sanity shortfalls)
      if (totalSanityLoss > 0) {
        this.gameState.modifyResource('sanity', -totalSanityLoss);
      }
    } else {
      // Apply effects only (no cost)
      if (selectedChoice.effects) {
        for (const effect of selectedChoice.effects) {
          for (const [resource, amount] of Object.entries(effect)) {
            if (amount !== undefined && amount !== 0) {
              this.gameState.modifyResource(resource as any, amount);
            }
          }
        }
      }
    }
    
    this.currentStep = 4;
    await this.executeStep();
  }
  
  // Step 5: Complete - shuffle discard into deck
  private step5_Complete(): void {
    this.deck.reshuffleDiscard();
    this.onComplete();
  }
  
  private showModal(title: string, message: string, _options: any[]): Promise<void> {
    return new Promise((resolve) => {
      // Clean up any tooltips before showing modal
      document.querySelectorAll('.game-tooltip').forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        padding: 30px;
        max-width: 500px;
        width: 90%;
      `;
      
      const titleElem = document.createElement('h2');
      titleElem.style.cssText = `
        font-size: 24px;
        color: #fff;
        margin-bottom: 15px;
      `;
      titleElem.textContent = title;
      
      const messageElem = document.createElement('p');
      messageElem.style.cssText = `
        font-size: 20px;
        color: #ccc;
        line-height: 1.6;
        margin-bottom: 20px;
      `;
      // Replace resource and stat names with symbols
      const messageWithSymbols = replaceResourceNamesWithSymbols(message);
      messageElem.innerHTML = messageWithSymbols;
      
      const button = document.createElement('button');
      button.style.cssText = `
        padding: 12px 24px;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        color: #fff;
        cursor: pointer;
        font-size: 16px;
        transition: all 0.2s ease;
      `;
      button.textContent = 'Continue';
      button.addEventListener('mouseenter', () => {
        button.style.background = 'rgba(255, 255, 255, 0.2)';
      });
      button.addEventListener('mouseleave', () => {
        button.style.background = 'rgba(255, 255, 255, 0.1)';
      });
      button.addEventListener('click', () => {
        document.body.removeChild(overlay);
        resolve();
      });
      
      modal.appendChild(titleElem);
      modal.appendChild(messageElem);
      modal.appendChild(button);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }
  
  // Check if a cost can be afforded in the Regrets phase
  // Health and Sanity are always payable (can go negative)
  // Other resources must be fully available
  private canAffordRegretsCost(cost: Cost | undefined): boolean {
    if (!cost) {
      return true; // No cost means always affordable
    }
    
    // Check each resource requirement
    if (cost.money !== undefined && cost.money > 0) {
      if (this.gameState.getResource('money') < cost.money) {
        return false;
      }
    }
    if (cost.food !== undefined && cost.food > 0) {
      if (this.gameState.getResource('food') < cost.food) {
        return false;
      }
    }
    if (cost.motivation !== undefined && cost.motivation > 0) {
      if (this.gameState.getResource('motivation') < cost.motivation) {
        return false;
      }
    }
    if (cost.memory !== undefined && cost.memory > 0) {
      if (this.gameState.getResource('memory') < cost.memory) {
        return false;
      }
    }
    if (cost.connections !== undefined && cost.connections > 0) {
      if (this.gameState.getResource('connections') < cost.connections) {
        return false;
      }
    }
    // Health and Sanity are always payable (can go negative)
    // Time cost is not used in Regrets phase
    
    return true;
  }
  
  private showChoiceModal(title: string, message: string, choices: Array<{ name: string; cost?: any; effects?: any[] }>): Promise<number> {
    return new Promise((resolve) => {
      // Clean up any tooltips before showing modal
      document.querySelectorAll('.game-tooltip').forEach(tooltip => {
        if (tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      const overlay = document.createElement('div');
      overlay.className = 'modal-overlay';
      overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.8);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
      `;
      
      const modal = document.createElement('div');
      modal.style.cssText = `
        background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 16px;
        padding: 30px;
        max-width: 500px;
        width: 90%;
      `;
      
      const titleElem = document.createElement('h2');
      titleElem.style.cssText = `
        font-size: 24px;
        color: #fff;
        margin-bottom: 15px;
      `;
      titleElem.textContent = title;
      
      const messageElem = document.createElement('p');
      messageElem.style.cssText = `
        font-size: 20px;
        color: #ccc;
        line-height: 1.6;
        margin-bottom: 20px;
      `;
      // Replace resource and stat names with symbols
      const messageWithSymbols = replaceResourceNamesWithSymbols(message);
      messageElem.innerHTML = messageWithSymbols;
      
      // Show current resources
      const resourcesContainer = document.createElement('div');
      resourcesContainer.style.cssText = `
        background: rgba(0, 0, 0, 0.3);
        border: 1px solid rgba(255, 255, 255, 0.1);
        border-radius: 8px;
        padding: 15px;
        margin-bottom: 20px;
        font-size: 16px;
        color: #ccc;
      `;
      const resourcesTitle = document.createElement('div');
      resourcesTitle.style.cssText = 'font-weight: bold; color: #fff; margin-bottom: 8px;';
      resourcesTitle.textContent = 'Your Current Resources:';
      resourcesContainer.appendChild(resourcesTitle);
      
      const resourcesList = document.createElement('div');
      resourcesList.style.cssText = 'display: flex; flex-wrap: wrap; gap: 12px;';
      
      const resourceTypes: Array<{ type: string; label: string }> = [
        { type: 'money', label: 'Money' },
        { type: 'food', label: 'Food' },
        { type: 'motivation', label: 'Motivation' },
        { type: 'memory', label: 'Memory' },
        { type: 'connections', label: 'Connections' },
        { type: 'health', label: 'Health' },
        { type: 'sanity', label: 'Sanity' },
      ];
      
      resourceTypes.forEach(({ type, label }) => {
        const value = this.gameState.getResource(type as any);
        const icon = getResourceIcon(type as any);
        const resourceElem = document.createElement('span');
        resourceElem.style.cssText = 'display: inline-flex; align-items: center; gap: 4px;';
        resourceElem.innerHTML = `${label} ${icon}: <strong>${value}</strong>`;
        resourcesList.appendChild(resourceElem);
      });
      
      resourcesContainer.appendChild(resourcesList);
      
      const choicesContainer = document.createElement('div');
      choicesContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 10px;
        margin-bottom: 20px;
      `;
      
      choices.forEach((choice, index) => {
        // Last choice should always be available (fallback option with no cost)
        // This prevents the game from getting stuck
        const isLastChoice = index === choices.length - 1;
        const canAfford = isLastChoice ? true : this.canAffordRegretsCost(choice.cost as Cost | undefined);
        const button = document.createElement('button');
        button.style.cssText = `
          padding: 15px;
          background: rgba(255, 255, 255, 0.1);
          border: 2px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: ${canAfford ? '#fff' : '#888'};
          cursor: ${canAfford ? 'pointer' : 'not-allowed'};
          font-size: 18px;
          text-align: left;
          transition: all 0.2s ease;
          opacity: ${canAfford ? '1' : '0.5'};
        `;
        // Replace resource names with icons in choice name
        const choiceNameWithSymbols = replaceResourceNamesWithSymbols(choice.name);
        button.innerHTML = choiceNameWithSymbols;
        
        if (canAfford) {
          button.addEventListener('mouseenter', () => {
            button.style.background = 'rgba(255, 255, 255, 0.2)';
            button.style.borderColor = 'rgba(255, 255, 255, 0.4)';
          });
          button.addEventListener('mouseleave', () => {
            button.style.background = 'rgba(255, 255, 255, 0.1)';
            button.style.borderColor = 'rgba(255, 255, 255, 0.2)';
          });
          button.addEventListener('click', () => {
            document.body.removeChild(overlay);
            resolve(index);
          });
        } else {
          // Disable pointer events for unaffordable choices
          button.style.pointerEvents = 'none';
          button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }, true);
        }
        choicesContainer.appendChild(button);
      });
      
      modal.appendChild(titleElem);
      modal.appendChild(messageElem);
      modal.appendChild(resourcesContainer);
      modal.appendChild(choicesContainer);
      overlay.appendChild(modal);
      document.body.appendChild(overlay);
    });
  }
}

